import sessionStore from '../../../data/sessionPersistentStore'
import { createSlice } from '@reduxjs/toolkit'
import { createSelector } from '@reduxjs/toolkit'
import dayjs from 'dayjs'

/**
 * An array containing two ISO string dates:
 * - The first date represents the start of the current month.
 * - The second date represents the current date and time.
 *
 * @type {string[]}
 */
const initDates = [
  dayjs(new Date()).startOf('month').toISOString(),
  dayjs(new Date()).toISOString()
]

/**
 * The initial state for the range of days slice.
 * 
 * @property {Object} initialState - The initial state object.
 * @property {Array|Object} initialState.dates - The range of dates to be used.
 *   If the session store has an empty range date, it defaults to `initDates`.
 *   Otherwise, it retrieves the range date from the session store.
 */
const initialState = {
  dates: sessionStore.isEmptyRangeDate()
    ? initDates
    : sessionStore.getRangeDate()
}

/**
 * A Redux slice for managing a range of days.
 * 
 * @namespace rangeOfDaysSlice
 * @property {string} name - The name of the slice.
 * @property {Object} initialState - The initial state of the slice.
 * @property {Object} reducers - The reducer functions for the slice.
 * @property {Function} reducers.addRangeOfDays - Adds a range of days to the state.
 *   @param {Object} state - The current state of the slice.
 *   @param {Object} action - The dispatched action.
 *   @param {Array} action.payload - An array containing two date strings.
 *   @returns {void}
 *   @description Converts the provided dates to ISO strings, adjusts them to the start and end of the day, 
 *   and updates the state and session storage with the new range of dates.
 * @property {Function} reducers.resetRangeOfDays - Resets the range of days in the state.
 *   @param {Object} state - The current state of the slice.
 *   @returns {void}
 *   @description Clears the range of dates in the state and removes it from session storage.
 */
const rangeOfDaysSlice = createSlice({
  name: 'date',
  initialState,
  reducers: {
    /**
     * Adds a range of days to the state and updates the session store.
     *
     * @param {Object} state - The current state of the slice.
     * @param {Object} action - The dispatched action containing the payload.
     * @param {Array} action.payload - An array containing two date strings.
     * @param {string|null} action.payload[0] - The start date of the range.
     * @param {string|null} action.payload[1] - The end date of the range.
     *
     * @description
     * This function validates the provided dates, converts them to ISO strings
     * with the start and end of the day respectively, and updates the state
     * and session store with the new range of dates.
     */
    addRangeOfDays(state, action) {
      const dates = action.payload
      const newDates = [
        dayjs(dates[0]).isValid()
          ? dayjs(dates[0]).startOf('day').toISOString()
          : null,
        dayjs(dates[1]).isValid()
          ? dayjs(dates[1]).endOf('day').toISOString()
          : null
      ]
      state.dates = newDates
      sessionStore.setRangeDate(newDates)
    },
    /**
     * Resets the range of days in the state and removes the stored range date from the session storage.
     *
     * @param {Object} state - The current state of the slice.
     * @param {Array} state.dates - An array representing the range of dates, which will be reset to [null, null].
     */
    resetRangeOfDays(state) {
      sessionStore.removeRangeDate()
      state.dates = [null, null]
    }
  }
})

export const { addRangeOfDays, resetRangeOfDays } =
  rangeOfDaysSlice.actions

/**
 * Selector function to retrieve the range of days from the UI state.
 *
 * @param {Object} state - The global state object of the application.
 * @returns {any} The range of days from the UI state.
 */
const getDaySelector = state => state.ui.rangeOfDay

/**
 * Selector to retrieve the current dates from the state.
 * It maps over the `state.dates` array and converts each date
 * into a JavaScript Date object using `dayjs`. If a date is null or undefined,
 * it returns null for that entry. If `state.dates` is not defined, it defaults
 * to an array with two null values.
 *
 * @param {Object} state - The Redux state.
 * @returns {Array<Date|null>} An array of JavaScript Date objects or null values.
 */
export const getCurrentDate = createSelector([getDaySelector], state =>
  state.dates
    ? state.dates?.map(date => (date ? dayjs(date)?.toDate() : null))
    : [null, null]
)

/**
 * The reducer function for the rangeOfDaysSlice.
 * This function is used to handle state transitions for the range of days feature
 * in the application's Redux store.
 *
 * @type {import('@reduxjs/toolkit').Reducer}
 */
const reducer = rangeOfDaysSlice.reducer
export default reducer
